import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Wrench, Truck, Package, CheckCircle2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  selecionado: { label: "Selecionado", color: "#94a3b8", next: "em_preparacao" },
  em_preparacao: { label: "Em Preparação", color: "#3b82f6", next: "despachado" },
  despachado: { label: "Despachado", color: "#F97316", next: "entregue" },
  entregue: { label: "Entregue", color: "#16a34a", next: null },
};

const TIPO_LABEL = { "12_meses": "12 meses", mensal: "Mensal", renovacao: "Renovação", venda: "Venda" };

const fmtData = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
};

export default function LogisticaEquipamentos() {
  const [items, setItems] = useState([]);

  const carregar = async () => {
    const { data } = await api.get("/logistica/equipamentos-pendentes");
    setItems(data.items || []);
  };
  useEffect(() => { carregar(); }, []);

  const avancarStatus = async (eq) => {
    const proximo = STATUS_LABEL[eq.status]?.next;
    if (!proximo) return;
    try {
      await api.patch(`/logistica/equipamentos/${eq.id}/status`, { status: proximo });
      toast.success(`Status atualizado: ${STATUS_LABEL[proximo].label}`);
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const setStatus = async (eq, novo) => {
    try {
      await api.patch(`/logistica/equipamentos/${eq.id}/status`, { status: novo });
      toast.success(`Status: ${STATUS_LABEL[novo].label}`);
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  return (
    <div className="space-y-5" data-testid="logistica-page">
      <div className="flex items-center gap-3">
        <Wrench size={28} style={{ color: "#1B3A6B" }} />
        <div>
          <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>Logística / Oficina</h1>
          <p className="text-sm text-muted-foreground">Equipamentos pendentes de preparação · {items.length} contratos</p>
        </div>
      </div>

      {items.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground" data-testid="logistica-vazia">
          <Package size={48} className="mx-auto mb-3 opacity-30" />
          <div>Nenhum equipamento pendente. Todos os contratos foram entregues!</div>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((grupo) => (
            <Card key={grupo.contrato_id} className="p-5" data-testid={`grupo-${grupo.contrato_id}`}>
              <div className="flex items-center justify-between mb-3 pb-3 border-b">
                <div>
                  <div className="font-bold text-lg" style={{ color: "#1B3A6B" }}>{grupo.cliente_nome}</div>
                  <div className="text-xs text-muted-foreground">
                    {TIPO_LABEL[grupo.tipo] || grupo.tipo} · Vendedor: {grupo.vendedor_nome} · Recebido: {fmtData(grupo.criado_em)}
                  </div>
                </div>
                <Badge variant="outline">{grupo.equipamentos.length} equipamentos</Badge>
              </div>
              <div className="space-y-2">
                {grupo.equipamentos.map((eq) => {
                  const s = STATUS_LABEL[eq.status];
                  return (
                    <div key={eq.id} className="flex items-center gap-3 p-3 rounded border bg-zinc-50" data-testid={`eq-${eq.id}`}>
                      <Package size={20} style={{ color: s.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{eq.nome} <span className="text-sm text-muted-foreground">× {eq.quantidade}</span></div>
                        {eq.descricao && <div className="text-xs text-muted-foreground">{eq.descricao}</div>}
                        {eq.observacoes_logistica && (
                          <div className="text-xs italic text-amber-700 mt-1">📝 {eq.observacoes_logistica}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={eq.status} onValueChange={(v) => setStatus(eq, v)}>
                          <SelectTrigger className="w-[150px]" data-testid={`status-select-${eq.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_LABEL).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {s.next && (
                          <Button size="sm" onClick={() => avancarStatus(eq)} className="text-white" style={{ background: s.color }} data-testid={`avancar-${eq.id}`}>
                            <ChevronRight size={12} className="mr-1" /> {STATUS_LABEL[s.next].label}
                          </Button>
                        )}
                        {!s.next && <CheckCircle2 size={20} className="text-green-600" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
